package br.com.htms.inspeccaocampo.auth

import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.ObraItem

/** Tipos de perfil de campo (independentes do role de sistema admin). */
object ProfileTypes {
    const val TST = "TST"
    const val ADM = "ADM"

    fun normalize(raw: Collection<String>?): List<String> {
        val hasTst = raw?.any { it.equals(TST, ignoreCase = true) } == true
        val hasAdm = raw?.any { it.equals(ADM, ignoreCase = true) } == true
        return buildList {
            if (hasTst) add(TST)
            if (hasAdm) add(ADM)
        }
    }

    fun fromEncoded(raw: String?): List<String> =
        normalize(
            raw?.split(',', ';', '|', ' ')
                ?.map { it.trim() }
                ?.filter { it.isNotEmpty() }
        )

    fun encode(types: Collection<String>): String = normalize(types).joinToString(",")

    fun label(types: Collection<String>): String {
        val n = normalize(types)
        return when {
            n.isEmpty() -> "—"
            n.size == 2 -> "TST + ADM"
            else -> n.first()
        }
    }
}

/** Usuário autenticado (sessão). */
data class AuthUser(
    val id: String,
    val username: String,
    val role: String,
    val profileTypes: List<String> = listOf(ProfileTypes.TST),
    val createdAt: Long? = null
) {
    val isAdmin: Boolean get() = role.equals("admin", ignoreCase = true)

    /** Tipos efetivos: admin de sistema vê tudo; campo vazio → TST. */
    val effectiveProfileTypes: List<String>
        get() = when {
            isAdmin -> listOf(ProfileTypes.TST, ProfileTypes.ADM)
            else -> ProfileTypes.normalize(profileTypes).ifEmpty { listOf(ProfileTypes.TST) }
        }

    fun canAccessForm(tipo: FormTipo): Boolean {
        if (isAdmin) return true
        val types = effectiveProfileTypes
        val hasTst = ProfileTypes.TST in types
        val hasAdm = ProfileTypes.ADM in types
        return when (tipo) {
            FormTipo.INSPECAO_CAMPO,
            FormTipo.COMUNICACAO_OCORRENCIAS -> hasTst
            FormTipo.RELATORIO_ALOJAMENTO,
            FormTipo.RELATORIO_FOTO_ALOJAMENTO,
            FormTipo.CHECKLIST_ALOJAMENTO -> hasAdm
        }
    }
}

data class AuthSession(
    val token: String,
    val expiresAt: Long,
    val user: AuthUser
)

data class RemoteInspection(
    val id: String,
    val localId: Long?,
    val createdAt: Long,
    val username: String?,
    val summaryTst: String,
    val summaryLocal: String,
    val summaryEfetivo: String?,
    val summaryObra: String? = null,
    val dataHorarioMs: Long? = null,
    /** Nome amigável do PDF (ex.: Inspecao_23-07-2026_Nome_Obra.pdf). */
    val pdfFileName: String? = null,
    /** Nome amigável do Excel. */
    val excelFileName: String? = null,
    /** Discriminador: INSPECAO_CAMPO | RELATORIO_FOTO_ALOJAMENTO | CHECKLIST_ALOJAMENTO | RELATORIO_ALOJAMENTO | COMUNICACAO_OCORRENCIAS */
    val formType: String? = null,
    val hasPdf: Boolean,
    val hasExcel: Boolean,
    val hasJson: Boolean
) {
    val formTipoRotulo: String
        get() = br.com.htms.inspeccaocampo.data.FormTipo.fromId(formType).rotuloCurto
}

data class AuthUserListItem(
    val id: String,
    val username: String,
    val role: String,
    val profileTypes: List<String> = listOf(ProfileTypes.TST),
    val createdAt: Long?
)

/**
 * API remota de autenticação / histórico / admin.
 * Web/Android: Worker Cloudflare. iOS: stub (sem login remoto).
 */
expect object AuthApi {
    /** true se a plataforma exige login (API base configurada). */
    fun isLoginRequired(): Boolean

    fun currentSession(): AuthSession?

    suspend fun restoreSession(): AuthSession?

    suspend fun login(username: String, password: String): Result<AuthSession>

    fun logout()

    suspend fun listInspections(): Result<List<RemoteInspection>>

    /**
     * Envia PDF + Excel (+ JSON opcional) após salvar localmente.
     * @return id remoto ou erro
     */
    suspend fun uploadInspection(
        localId: Long,
        createdAt: Long,
        summaryTst: String,
        summaryLocal: String,
        summaryEfetivo: String?,
        summaryObra: String?,
        dataHorarioMs: Long? = null,
        formType: String? = null,
        pdfFileName: String? = null,
        excelFileName: String? = null,
        pdfBytes: ByteArray?,
        excelBytes: ByteArray?,
        jsonText: String?
    ): Result<String>

    suspend fun downloadInspectionBytes(remoteId: String, kind: String): Result<ByteArray>

    /** Admin: remove inspeção remota (D1 + R2). */
    suspend fun deleteInspection(remoteId: String): Result<Unit>

    suspend fun listUsers(): Result<List<AuthUserListItem>>

    suspend fun createUser(
        username: String,
        password: String,
        role: String,
        profileTypes: List<String>
    ): Result<AuthUserListItem>

    suspend fun updateUser(
        userId: String,
        role: String? = null,
        profileTypes: List<String>? = null,
        password: String? = null
    ): Result<AuthUserListItem>

    suspend fun deleteUser(userId: String): Result<Unit>

    /** Obras ativas para o dropdown do formulário (API ou falha → Result.failure). */
    suspend fun listObrasAtivas(): Result<List<ObraItem>>

    /** Todas as obras (admin). */
    suspend fun listObrasAdmin(): Result<List<ObraItem>>

    suspend fun createObra(nome: String): Result<ObraItem>

    suspend fun updateObra(id: String, nome: String? = null, ativo: Boolean? = null): Result<ObraItem>
}
